import React, { useEffect, useState, useCallback } from "react";
import api, { downloadBlob } from "@/lib/api";
import {
  GraduationCap, ClipboardCheck, ShieldCheck, PillBottle, AlertOctagon,
  MessageSquareWarning, Target, Wrench, Users, HeartHandshake, TrendingUp, AlertTriangle, Download,
  Radio, NotebookPen, Pill, LogIn as LogInIcon, Award, FileDown, HardDrive, BellRing, BellOff, ShieldAlert, Trash2,
  Clock, Stethoscope, ChevronRight, CheckCircle2, RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { usePushNotifications } from "@/lib/pushNotifications";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import AiExecutiveBrief from "@/components/AiExecutiveBrief";
import OnboardingWidget from "@/components/OnboardingWidget";
import MedicationSafetyAlert from "@/components/MedicationSafetyAlert";
import TodaysOperationalSummary from "@/components/TodaysOperationalSummary";
import PersonAvatar from "@/components/PersonAvatar";
import DashboardHolidayWidget from "@/components/DashboardHolidayWidget";
import ReadinessSignoffDialog from "@/components/ReadinessSignoffDialog";
import MobileCqcReadiness from "@/components/mobile/MobileCqcReadiness";
import YearCloseNudge from "@/components/YearCloseNudge";

function KpiCard({ icon: Icon, label, value, sub, tone, testid, to, priority }) {
  const toneClass = {
    ok: "border-l-emerald-500",
    warn: "border-l-amber-500",
    danger: "border-l-red-500",
    info: "border-l-sky-500",
  }[tone || "info"];
  const priorityChip = {
    P0: "bg-red-100 text-red-800 border-red-300 dark:bg-red-500/20 dark:text-red-300",
    P1: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-300",
    P2: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-300",
  }[priority];
  const Wrapper = to ? Link : "div";
  return (
    <Wrapper
      to={to} data-testid={testid}
      className={`bg-white border border-slate-200 border-l-4 ${toneClass} p-4 hover:shadow-sm transition-shadow block`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold truncate">{label}</div>
          {priority && (
            <span
              className={`text-[9px] font-semibold uppercase tracking-widest border px-1 rounded-sm shrink-0 ${priorityChip}`}
              data-testid={`${testid}-priority`}
              title={`CQC priority ${priority}`}
            >
              {priority}
            </span>
          )}
        </div>
        <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-heading text-3xl font-semibold text-slate-900 tabular-nums">{value}</span>
      </div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </Wrapper>
  );
}

function ChartCard({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-4" data-testid={testid}>
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OutcomeStat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate:   "border-slate-200 bg-white text-slate-900",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    teal:    "border-teal-200 bg-teal-50/60 text-teal-900",
    amber:   "border-amber-200 bg-amber-50/60 text-amber-900",
    rose:    "border-rose-200 bg-rose-50/60 text-rose-900",
  };
  return (
    <div className={`border rounded-md p-3 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="text-xl font-heading font-semibold tabular-nums leading-none">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-1">{label}</div>
    </div>
  );
}


// Sentinel returned by a wrapped .catch — lets the .then handler
// distinguish a genuine successful response from a transient failure
// of the two core KPI endpoints (/dashboard/stats + /dashboard/trends)
// without silently coercing a failure into zero values. Module-scoped so
// its reference identity is stable across renders (no React Hook dep warning).
const CORE_FAIL = { __failed: true, data: null };

export default function Dashboard() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "manager";
  const { supported: pushSupported, subscribed: pushSubscribed, enable: enablePush, disable: disablePush } = usePushNotifications(isManager);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [handover, setHandover] = useState(null);
  const [readinessActions, setReadinessActions] = useState({});
  const [readinessSignoffOpen, setReadinessSignoffOpen] = useState(false);
  const [latestSignoff, setLatestSignoff] = useState(null);
  const [signoffHistory, setSignoffHistory] = useState([]);
  const [showSignoffHistory, setShowSignoffHistory] = useState(false);
  const [myActions, setMyActions] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [historyKey, setHistoryKey] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [readinessHistory, setReadinessHistory] = useState([]);
  const [kloeHistory, setKloeHistory] = useState([]);
  const [historyMode, setHistoryMode] = useState("overall");
  const [openExplain, setOpenExplain] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cdDiscrepancies, setCdDiscrepancies] = useState([]);
  const [pushHistory, setPushHistory] = useState([]);
  const [pushHistoryOpen, setPushHistoryOpen] = useState(false);
  const [missedDoses, setMissedDoses] = useState([]);
  const [nextDoses, setNextDoses] = useState([]);
  const [todaySummary, setTodaySummary] = useState(null);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  const [prnTrend, setPrnTrend] = useState(null);
  const [prnTrendHistory, setPrnTrendHistory] = useState(null);
  const [outcomesSummary, setOutcomesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadDashboard = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      api.get("/dashboard/stats").catch(() => CORE_FAIL),
      api.get("/dashboard/trends").catch(() => CORE_FAIL),
      api.get("/dashboard/handover").catch(() => ({ data: { items: [] } })),
      api.get("/dashboard/readiness").catch(() => ({ data: null })),
      api.get("/dashboard/readiness/history").catch(() => ({ data: [] })),
      api.get("/dashboard/readiness/history?kloe=true").catch(() => ({ data: [] })),
      api.get("/uploads/usage").catch(() => ({ data: null })),
      api.get("/emar/cd-ledger/discrepancies").catch(() => ({ data: { entries: [] } })),
      api.get("/uploads/cleanup/preview?days=90").catch(() => ({ data: null })),
      api.get("/push/history?limit=20").catch(() => ({ data: { items: [] } })),
      api.get("/emar/missed-doses").catch(() => ({ data: { missed: [] } })),
      api.get("/emar/next-doses?within_minutes=180&limit=5").catch(() => ({ data: { upcoming: [] } })),
      api.get("/emar/today-summary").catch(() => ({ data: null })),
      api.get("/dashboard/upcoming-appointments?days=14").catch(() => ({ data: { items: [] } })),
      api.get("/dashboard/prn-trend").catch(() => ({ data: null })),
      api.get("/dashboard/prn-trend-history?weeks=12").catch(() => ({ data: null })),
      api.get("/readiness-actions").catch(() => ({ data: { by_key: {} } })),
      api.get("/dashboard/readiness/signoffs/latest").catch(() => ({ data: { item: null } })),
      api.get("/dashboard/readiness/signoffs").catch(() => ({ data: [] })),
      api.get("/auth/users").catch(() => ({ data: [] })),
      api.get("/readiness-actions/my").catch(() => ({ data: { items: [], open_count: 0, overdue_count: 0 } })),
      api.get("/outcomes/summary/manager").catch(() => ({ data: null })),
    ])
      .then(([s, t, h, r, rh, kh, u, cd, cp, ph, md, nd, tsum, ap, pt, pth, ra, sl, sh, ul, my, oc]) => {
        // If either core KPI endpoint failed, surface an explicit error
        // state instead of leaving the panel on "Loading KPIs…" forever.
        // We deliberately do NOT setStats() here so healthy re-mounts keep
        // showing the previous good values while the retry is in flight.
        if (s?.__failed || t?.__failed) {
          setLoadError(true);
          return;
        }
        setLatestSignoff(sl.data?.item || null);
        setSignoffHistory(sh.data || []);
        setMyActions(my.data);
        setStats(s.data); setTrends(t.data); setHandover(h.data); setReadiness(r.data);
        setReadinessHistory(rh.data || []); setKloeHistory(kh.data || []);
        setUsage(u.data); setCdDiscrepancies(cd.data?.entries || []); setCleanupPreview(cp.data);
        setPushHistory(ph.data?.items || []);
        setMissedDoses(md.data?.missed || []);
        setNextDoses(nd.data?.upcoming || []);
        setTodaySummary(tsum.data || null);
        setUpcomingAppts(ap.data?.items || []);
        setPrnTrend(pt.data || null);
        setPrnTrendHistory(pth.data || null);
        setReadinessActions(ra.data?.by_key || {});
        setOutcomesSummary(oc.data || null);
        setStaffList(Array.isArray(ul.data) ? ul.data.filter((u) => ["carer","manager","admin"].includes(u.role)) : []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const runCleanup = async () => {
    if (!window.confirm(`Permanently delete ${cleanupPreview?.eligible_count || 0} soft-deleted attachment(s) older than 90 days? This cannot be undone.`)) return;
    const id = toast.loading("Purging attachments…");
    try {
      const r = await api.post("/uploads/cleanup/purge?days=90");
      toast.success(`Purged ${r.data.purged_count} file(s), freed ${(r.data.storage_bytes_freed / (1024 * 1024)).toFixed(1)} MB`, { id });
      const cp = await api.get("/uploads/cleanup/preview?days=90").catch(() => ({ data: null }));
      setCleanupPreview(cp.data);
      const u = await api.get("/uploads/usage").catch(() => ({ data: null }));
      setUsage(u.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed", { id });
    }
  };

  const togglePush = async () => {
    try {
      if (pushSubscribed) {
        await disablePush();
        toast.success("Desktop alerts disabled");
      } else {
        await enablePush();
        toast.success("Desktop alerts enabled — you'll get push notifications even when the tab is closed");
      }
    } catch (e) {
      toast.error(e?.message || "Could not enable notifications");
    }
  };

  const showExpiryBanner = stats && (stats.training.expired > 0 || stats.training.expiring_soon > 0);

  const handleExportEvidencePack = async () => {
    const id = toast.loading("Preparing CQC Evidence Pack…");
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadBlob("/reports/evidence-pack", `ayobell-cqc-evidence-pack-${today}.pdf`);
      toast.success("Evidence Pack downloaded", { id });
    } catch (e) {
      toast.error("Export failed", { id });
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Toolbar — hidden on mobile/tablet since MobilePageHeader owns the title there. */}
      <div className="hidden lg:flex min-h-14 border-b border-slate-200 bg-white px-6 items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Executive Overview</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="dashboard-title">Manager Dashboard</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-slate-500 hidden md:flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>Live · updates on record change</span>
          </div>
          {isManager && pushSupported && (
            <Button
              onClick={togglePush}
              size="sm" variant="outline"
              data-testid="btn-toggle-push"
              className="h-9"
            >
              {pushSubscribed
                ? (<><BellOff className="w-3.5 h-3.5 mr-1.5" /> Disable alerts</>)
                : (<><BellRing className="w-3.5 h-3.5 mr-1.5" /> Enable desktop alerts</>)}
            </Button>
          )}
          <Button
            onClick={handleExportEvidencePack}
            size="sm"
            data-testid="btn-export-evidence-pack"
            className="h-9 bg-slate-900 hover:bg-slate-800"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CQC Evidence Pack
          </Button>
        </div>
      </div>

      {/* Mobile / tablet compact toolbar — evidence pack export only,
          keeps the primary action within thumb-reach on phones + tablets. */}
      <div className="lg:hidden flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 bg-white">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Executive</div>
          <h1 className="font-heading text-base font-semibold truncate" data-testid="dashboard-title-mobile">Manager Dashboard</h1>
        </div>
        <Button
          onClick={handleExportEvidencePack}
          size="sm"
          data-testid="btn-export-evidence-pack-mobile"
          className="h-10 bg-slate-900 hover:bg-slate-800 shrink-0"
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Evidence
        </Button>
      </div>

      <div className="p-4 lg:p-6 space-y-4 lg:space-y-6">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="dashboard-loading">Loading KPIs…</div>
        ) : loadError || !stats ? (
          <div
            className="bg-white border border-slate-200 rounded-lg p-6 text-center max-w-md mx-auto space-y-3"
            data-testid="dashboard-load-error"
          >
            <div className="font-heading text-base font-semibold text-slate-900">
              Dashboard data couldn't be loaded.
            </div>
            <div className="text-sm text-slate-500">
              Please try again.
            </div>
            <Button
              onClick={loadDashboard}
              size="sm"
              className="h-9"
              data-testid="dashboard-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
            </Button>
          </div>
        ) : (
          <>
            {isManager && <YearCloseNudge />}

            <AiExecutiveBrief />
            <OnboardingWidget />

            <TodaysOperationalSummary />

            {isManager && outcomesSummary && (
              <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="outcomes-tile">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Person-Centred Outcomes</div>
                    <Link to="/outcomes" className="text-[10px] text-sky-700 hover:text-sky-900 inline-flex items-center gap-0.5" data-testid="outcomes-tile-link">
                      Open outcomes <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  <OutcomeStat label="Active" value={outcomesSummary.active} tone="slate" testid="outcomes-active" />
                  <OutcomeStat label="On track" value={outcomesSummary.on_track} tone="emerald" testid="outcomes-on-track" />
                  <OutcomeStat label="At risk" value={outcomesSummary.at_risk} tone="amber" testid="outcomes-at-risk" />
                  <OutcomeStat label="Reviews due" value={outcomesSummary.reviews_due} tone={outcomesSummary.reviews_due > 0 ? "rose" : "slate"} testid="outcomes-reviews-due" />
                  <OutcomeStat label="Achieved this month" value={outcomesSummary.achieved_this_month} tone="teal" testid="outcomes-achieved" />
                </div>
              </section>
            )}

            {myActions && myActions.items && myActions.items.length > 0 && (
              <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="my-actions">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My Readiness Actions</div>
                    <span className="text-[10px] text-slate-500" data-testid="my-actions-count">
                      <b>{myActions.open_count}</b> open{myActions.overdue_count > 0 && <> · <span className="text-red-700 font-semibold">{myActions.overdue_count} overdue</span></>}
                    </span>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100" data-testid="my-actions-list">
                  {myActions.items.slice(0, 5).map((a) => {
                    const tone = a.status === "overdue" ? "border-l-red-500 bg-red-50"
                      : a.status === "completed" ? "border-l-emerald-500 bg-emerald-50/40"
                      : a.status === "awaiting_evidence" ? "border-l-amber-500 bg-amber-50/40"
                      : a.status === "in_progress" ? "border-l-sky-500 bg-sky-50/40"
                      : "border-l-slate-300";
                    const statusLabel = {
                      not_started: "Not Started",
                      in_progress: "In Progress",
                      awaiting_evidence: "Awaiting Evidence",
                      completed: "Completed",
                      overdue: "Overdue",
                    }[a.status] || a.status;
                    return (
                      <li key={a.action_key} className={`flex items-center gap-3 py-2 pl-3 pr-1 border-l-2 ${tone}`} data-testid={`my-action-${a.action_key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{a.action_text}</div>
                          <div className="text-[11px] text-slate-500">{a.area} · due {a.due_date || "no date"}</div>
                        </div>
                        <select
                          value={a.status}
                          onChange={async (e) => {
                            try {
                              await api.patch(`/readiness-actions/${a.action_key}/status`, { status: e.target.value });
                              const r = await api.get("/readiness-actions/my");
                              setMyActions(r.data);
                            } catch { toast.error("Could not save"); }
                          }}
                          className="text-[11px] h-7 rounded border border-slate-300 bg-white px-1"
                          data-testid={`my-action-status-${a.action_key}`}
                        >
                          <option value="not_started">Not Started</option>
                          <option value="in_progress">In Progress</option>
                          <option value="awaiting_evidence">Awaiting Evidence</option>
                          <option value="completed">Completed</option>
                          <option value="overdue">Overdue</option>
                        </select>
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 w-24 text-right">{statusLabel}</span>
                      </li>
                    );
                  })}
                </ul>
                {myActions.items.length > 5 && (
                  <div className="text-[11px] text-slate-500 mt-2">+ {myActions.items.length - 5} more assigned</div>
                )}
              </section>
            )}

            {readiness && (() => {
              const bandStyles = {
                excellent:    { ring: "border-emerald-500", pill: "bg-emerald-100 text-emerald-800 border-emerald-300", bar: "bg-emerald-500", text: "text-emerald-700" },
                strong:       { ring: "border-teal-500",    pill: "bg-teal-100 text-teal-800 border-teal-300",          bar: "bg-teal-500",    text: "text-teal-700" },
                developing:   { ring: "border-amber-500",   pill: "bg-amber-100 text-amber-900 border-amber-300",       bar: "bg-amber-500",   text: "text-amber-700" },
                improvement:  { ring: "border-orange-500",  pill: "bg-orange-100 text-orange-900 border-orange-300",    bar: "bg-orange-500",  text: "text-orange-700" },
                significant:  { ring: "border-red-500",     pill: "bg-red-100 text-red-800 border-red-300",             bar: "bg-red-600",     text: "text-red-700" },
              };
              const bs = bandStyles[readiness.band] || bandStyles.developing;

              // Upsert handler shared by mobile actions primitive — same POST endpoint desktop uses.
              const upsertMobileAction = async (r, persisted, patch) => {
                try {
                  const res = await api.post("/readiness-actions/upsert", {
                    action_key: r.action_key,
                    area: r.area,
                    action_text: r.action,
                    priority: r.priority,
                    module: r.module,
                    owner_id: persisted?.owner_id || null,
                    owner_name: persisted?.owner_name || null,
                    due_date: persisted?.due_date || null,
                    status: persisted?.status || "not_started",
                    ...patch,
                  });
                  setReadinessActions((prev) => ({ ...prev, [r.action_key]: res.data }));
                } catch { toast.error("Could not save"); }
              };
              const openActionHistoryMobile = async (r) => {
                try {
                  const res = await api.get(`/readiness-actions/${r.action_key}/history`);
                  setHistoryKey(r.action_key);
                  setHistoryEntries(res.data?.history || []);
                } catch { toast.error("No history yet"); }
              };
              const exportReadinessPdfMobile = async () => {
                const t = toast.loading("Preparing readiness report…");
                try {
                  await downloadBlob("/dashboard/readiness/pdf", `ayobell-cqc-readiness-${new Date().toISOString().slice(0,10)}.pdf`);
                  toast.success("Readiness report downloaded", { id: t });
                } catch { toast.error("Export failed", { id: t }); }
              };
              const boardReviewPdfMobile = async () => {
                const t = toast.loading("Assembling board review pack…");
                try {
                  await downloadBlob("/dashboard/board-review/pdf", `ayobell-board-review-${new Date().toISOString().slice(0,10)}.pdf`);
                  toast.success("Board review pack downloaded", { id: t });
                } catch { toast.error("Export failed", { id: t }); }
              };
              const downloadLatestSignedMobile = async () => {
                if (!latestSignoff) return;
                const t = toast.loading("Downloading signed PDF…");
                try {
                  await downloadBlob(`/dashboard/readiness/signoffs/${latestSignoff.id}/pdf`, `ayobell-readiness-signed-${latestSignoff.signed_at.slice(0,10)}.pdf`);
                  toast.success("Signed PDF downloaded", { id: t });
                } catch { toast.error("Download failed", { id: t }); }
              };
              const downloadSnapshotPdfMobile = async (s) => {
                const t = toast.loading("Downloading signed PDF…");
                try {
                  await downloadBlob(`/dashboard/readiness/signoffs/${s.id}/pdf`, `ayobell-readiness-signed-${s.signed_at.slice(0,10)}-${s.id.slice(0,8)}.pdf`);
                  toast.success("Signed PDF downloaded", { id: t });
                } catch { toast.error("Download failed", { id: t }); }
              };

              return (
              <>
                {/* Mobile / tablet CQC Readiness — <lg only. Batch 7A. */}
                <div className="lg:hidden">
                  <MobileCqcReadiness
                    readiness={readiness}
                    latestSignoff={latestSignoff}
                    signoffHistory={signoffHistory}
                    readinessActions={readinessActions}
                    staffList={staffList}
                    canManage={isManager}
                    onExportPdf={exportReadinessPdfMobile}
                    onBoardReviewPdf={boardReviewPdfMobile}
                    onOpenSignDialog={() => setReadinessSignoffOpen(true)}
                    onDownloadLatestPdf={downloadLatestSignedMobile}
                    onUpsertAction={upsertMobileAction}
                    onOpenActionHistory={openActionHistoryMobile}
                    onDownloadSnapshotPdf={downloadSnapshotPdfMobile}
                  />
                </div>
              <section data-testid="readiness-section" className="hidden lg:block bg-white border border-slate-200 rounded-lg">
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    <Award className="w-3 h-3" /> CQC Inspection Readiness Report
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    AYOBELL Care · Generated {new Date(readiness.generated_at || Date.now()).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="text-[11px] text-slate-400 italic">Internal governance and inspection-preparedness assessment</div>
                </div>

                {/* Score hero */}
                <div className="px-5 py-4 flex items-start gap-6 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className={`w-28 h-28 rounded-lg flex items-center justify-center border-4 ${bs.ring}`}>
                      <div className="text-center">
                        <div className="font-heading text-3xl font-semibold tabular-nums leading-none" data-testid="readiness-score">{readiness.score}</div>
                        <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-1">/ 100</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">AYOBELL CQC Readiness Score</div>
                      <div className={`font-heading text-xl font-semibold ${bs.text} mt-0.5`} data-testid="readiness-band">
                        {readiness.band_label || "Developing Readiness"}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700"><b>{readiness.recommendations?.length || 0}</b> Priority Actions</span>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800"><b>{readiness.strong_count ?? 0}</b> Areas Performing Strongly</span>
                        <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-900"><b>{readiness.attention_count ?? 0}</b> Areas Requiring Attention</span>
                      </div>
                      <Button
                        size="sm" variant="outline"
                        onClick={async () => {
                          const t = toast.loading("Preparing readiness report…");
                          try {
                            await downloadBlob("/dashboard/readiness/pdf", `ayobell-cqc-readiness-${new Date().toISOString().slice(0,10)}.pdf`);
                            toast.success("Readiness report downloaded", { id: t });
                          } catch { toast.error("Export failed", { id: t }); }
                        }}
                        data-testid="btn-download-readiness"
                        className="h-8 mt-2"
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1.5" /> Export PDF
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          const t = toast.loading("Assembling board review pack…");
                          try {
                            await downloadBlob("/dashboard/board-review/pdf", `ayobell-board-review-${new Date().toISOString().slice(0,10)}.pdf`);
                            toast.success("Board review pack downloaded", { id: t });
                          } catch { toast.error("Export failed", { id: t }); }
                        }}
                        data-testid="btn-board-review"
                        className="h-8 mt-2 ml-2 bg-sky-700 hover:bg-sky-800"
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1.5" /> Prepare for Board Meeting
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setReadinessSignoffOpen(true)}
                        data-testid="btn-sign-readiness"
                        className="h-8 mt-2 ml-2 border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Sign Readiness Assessment
                      </Button>
                      {latestSignoff && (
                        <div className="mt-2 ml-2 inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-900 px-2.5 py-1 text-[11px]"
                             data-testid="readiness-signed-chip">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <div>
                            <div className="font-semibold">Readiness Assessment Signed</div>
                            <div className="text-emerald-800/80">
                              {latestSignoff.signed_by_name} · {latestSignoff.signed_by_role}
                              {" · "}
                              {new Date(latestSignoff.signed_at).toLocaleString(undefined, {
                                day: "2-digit", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              const t = toast.loading("Downloading signed PDF…");
                              try {
                                await downloadBlob(`/dashboard/readiness/signoffs/${latestSignoff.id}/pdf`, `ayobell-readiness-signed-${latestSignoff.signed_at.slice(0,10)}.pdf`);
                                toast.success("Signed PDF downloaded", { id: t });
                              } catch { toast.error("Download failed", { id: t }); }
                            }}
                            className="ml-1 text-emerald-700 hover:text-emerald-900 underline text-[11px]"
                            data-testid="btn-download-latest-signed"
                          >PDF</button>
                        </div>
                      )}
                      {typeof readiness.delta === "number" && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium" data-testid="readiness-delta">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${
                            readiness.delta > 0 ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                            : readiness.delta < 0 ? "bg-red-50 text-red-800 border-red-300"
                            : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                            {readiness.delta > 0 ? "▲" : readiness.delta < 0 ? "▼" : "→"} {readiness.delta > 0 ? "+" : ""}{readiness.delta} pts
                          </span>
                          <span className="text-slate-500">since last assessment{readiness.delta_since ? ` (${new Date(readiness.delta_since).toLocaleDateString(undefined, { day: "2-digit", month: "short" })})` : ""}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Disclaimer info panel */}
                  <div className="flex-1 min-w-[300px] bg-sky-50 border border-sky-200 rounded-md p-3 text-[12px] text-sky-900 leading-snug" data-testid="readiness-disclaimer">
                    <div className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-sky-200 text-sky-800 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">i</span>
                      <div>
                        <div className="font-semibold mb-0.5">About this score</div>
                        <div>AYOBELL CQC Inspection Readiness is an internal governance and inspection-preparedness assessment. It does not constitute a CQC rating, inspection outcome, regulatory judgement, approval or endorsement.</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assessment History — collapsible list of signed snapshots */}
                {signoffHistory.length > 0 && (
                  <div className="px-5 py-3 border-t border-slate-100" data-testid="signoff-history-section">
                    <button
                      onClick={() => setShowSignoffHistory((v) => !v)}
                      className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold hover:text-slate-800 flex items-center gap-1"
                      data-testid="btn-toggle-signoff-history"
                    >
                      Assessment History ({signoffHistory.length}) {showSignoffHistory ? "▲" : "▼"}
                    </button>
                    {showSignoffHistory && (
                      <ul className="mt-2 divide-y divide-slate-100 text-[12px]" data-testid="signoff-history-list">
                        {signoffHistory.map((s) => (
                          <li key={s.id} className="py-1.5 flex items-center justify-between gap-2" data-testid={`signoff-history-${s.id}`}>
                            <div className="flex-1 min-w-0">
                              <span className="tabular-nums text-slate-700">{new Date(s.signed_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>
                              <span className="mx-2 text-slate-300">·</span>
                              <span className="font-semibold tabular-nums text-slate-900">{s.score}</span>
                              <span className="ml-2 text-slate-600">{s.band_label}</span>
                              <span className="mx-2 text-slate-300">·</span>
                              <span className="text-slate-600">{s.signed_by_name} · {s.signed_by_role}</span>
                            </div>
                            <span className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded">Signed</span>
                            <button
                              onClick={async () => {
                                const t = toast.loading("Downloading signed PDF…");
                                try {
                                  await downloadBlob(`/dashboard/readiness/signoffs/${s.id}/pdf`, `ayobell-readiness-signed-${s.signed_at.slice(0,10)}-${s.id.slice(0,8)}.pdf`);
                                  toast.success("Signed PDF downloaded", { id: t });
                                } catch { toast.error("Download failed", { id: t }); }
                              }}
                              className="text-emerald-700 hover:text-emerald-900 text-[11px] underline"
                              data-testid={`btn-history-pdf-${s.id}`}
                            >PDF</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Governance Readiness Breakdown */}
                <div className="px-5 py-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Governance Readiness Breakdown</div>
                  <div className="text-[11px] text-slate-500 mb-3">
                    Calculated from six key governance and operational readiness areas monitored within AYOBELL Care.
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full text-[12px]" data-testid="readiness-breakdown">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-3 font-semibold">Governance Area</th>
                        <th className="py-2 pr-3 font-semibold text-right">Current Score</th>
                        <th className="py-2 pr-3 font-semibold text-right">Weight</th>
                        <th className="py-2 pr-3 font-semibold">Readiness Status</th>
                        <th className="py-2 pr-3 font-semibold">Regulatory Alignment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readiness.breakdown.map((b) => {
                        const s = bandStyles[b.status?.key] || bandStyles.developing;
                        return (
                          <tr key={b.area} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`readiness-row-${b.module}`}>
                            <td className="py-2 pr-3">
                              <Link to={`/${b.module}`} className="text-slate-900 hover:text-sky-700 hover:underline font-medium">{b.area}</Link>
                              <div className="h-1.5 bg-slate-100 rounded mt-1 w-40">
                                <div className={`h-1.5 rounded ${s.bar}`} style={{ width: `${Math.max(2, Math.min(100, b.score))}%` }} />
                              </div>
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums font-semibold text-slate-900">{b.score}%</td>
                            <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{b.weight}%</td>
                            <td className="py-2 pr-3">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${s.pill}`}>
                                {b.status?.label || "Developing"}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-slate-600">{(b.regulatory_alignment || []).join(" / ") || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 italic">
                    Regulatory Alignment maps each area to relevant CQC assessment themes. AYOBELL&apos;s internal weightings are not CQC-prescribed.
                  </div>
                </div>

                {/* Priority Actions + Evidence Readiness */}
                <div className="px-5 py-4 border-t border-slate-100 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Priority Actions Before Inspection</div>
                    {readiness.recommendations.length === 0 ? (
                      <div className="text-xs text-slate-500" data-testid="readiness-no-actions">No outstanding priority actions.</div>
                    ) : (
                      <table className="w-full text-[12px]" data-testid="readiness-actions">
                        <thead>
                          <tr className="text-left text-[9px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <th className="py-1 pr-2 font-semibold">Priority</th>
                            <th className="py-1 pr-2 font-semibold">Action</th>
                            <th className="py-1 pr-2 font-semibold">Owner</th>
                            <th className="py-1 pr-2 font-semibold">Due</th>
                            <th className="py-1 font-semibold">Status</th>
                            <th className="py-1 pl-2 font-semibold text-right">Log</th>
                          </tr>
                        </thead>
                        <tbody>
                          {readiness.recommendations.map((r, i) => {
                            const persisted = readinessActions[r.action_key] || {};
                            const upsert = async (patch) => {
                              try {
                                const res = await api.post("/readiness-actions/upsert", {
                                  action_key: r.action_key,
                                  area: r.area,
                                  action_text: r.action,
                                  priority: r.priority,
                                  module: r.module,
                                  owner_id: persisted.owner_id || null,
                                  owner_name: persisted.owner_name || null,
                                  due_date: persisted.due_date || null,
                                  status: persisted.status || "not_started",
                                  ...patch,
                                });
                                setReadinessActions((prev) => ({ ...prev, [r.action_key]: res.data }));
                              } catch { toast.error("Could not save"); }
                            };
                            const statusColour = {
                              not_started:       "bg-slate-100 text-slate-700 border-slate-300",
                              in_progress:       "bg-sky-100 text-sky-800 border-sky-300",
                              awaiting_evidence: "bg-amber-100 text-amber-900 border-amber-300",
                              completed:         "bg-emerald-100 text-emerald-800 border-emerald-300",
                              overdue:           "bg-red-100 text-red-800 border-red-300",
                            }[persisted.status || "not_started"];
                            return (
                              <tr key={r.action_key} className="border-b border-slate-100 align-top" data-testid={`readiness-action-${i}`}>
                                <td className="py-2 pr-2">
                                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                    r.priority === "high" ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-800"
                                  }`}>{r.priority}</span>
                                </td>
                                <td className="py-2 pr-2">
                                  <Link to={`/${r.module}`} className="text-slate-800 hover:text-sky-700 hover:underline block">{r.action}</Link>
                                  <div className="text-[10px] text-slate-400">{r.area}</div>
                                </td>
                                <td className="py-2 pr-2">
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={persisted.owner_id || ""}
                                      onChange={(e) => {
                                        const st = staffList.find((s) => s.id === e.target.value);
                                        upsert({ owner_id: st?.id || null, owner_name: st?.name || null });
                                      }}
                                      className="text-[11px] h-7 rounded border border-slate-300 bg-white px-1 max-w-[130px]"
                                      data-testid={`action-owner-${i}`}
                                    >
                                      <option value="">Unassigned</option>
                                      {staffList.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name || s.email}</option>
                                      ))}
                                    </select>
                                    <div className="flex -space-x-1.5" data-testid={`action-owner-quickpick-${i}`}>
                                      {staffList.slice(0, 4).map((s) => {
                                        const selected = persisted.owner_id === s.id;
                                        return (
                                          <button
                                            key={s.id}
                                            type="button"
                                            title={`Assign to ${s.name || s.email}`}
                                            onClick={() => {
                                              if (selected) {
                                                upsert({ owner_id: null, owner_name: null });
                                              } else {
                                                upsert({ owner_id: s.id, owner_name: s.name || s.email });
                                              }
                                            }}
                                            className={`transition-transform ${selected ? "ring-2 ring-sky-500 scale-110 z-10" : "hover:scale-110 opacity-70 hover:opacity-100"} rounded-full`}
                                            data-testid={`action-owner-avatar-${i}-${s.id}`}
                                          >
                                            <PersonAvatar person={s} size="xs" ring />
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </td>
                                <td className="py-2 pr-2">
                                  <input
                                    type="date"
                                    value={persisted.due_date || ""}
                                    onChange={(e) => upsert({ due_date: e.target.value || null })}
                                    className="text-[11px] h-7 rounded border border-slate-300 bg-white px-1"
                                    data-testid={`action-due-${i}`}
                                  />
                                </td>
                                <td className="py-2">
                                  <select
                                    value={persisted.status || "not_started"}
                                    onChange={(e) => upsert({ status: e.target.value })}
                                    className={`text-[11px] h-7 rounded border px-1 font-semibold ${statusColour}`}
                                    data-testid={`action-status-${i}`}
                                  >
                                    <option value="not_started">Not Started</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="awaiting_evidence">Awaiting Evidence</option>
                                    <option value="completed">Completed</option>
                                    <option value="overdue">Overdue</option>
                                  </select>
                                </td>
                                <td className="py-2 pl-2 text-right">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        const res = await api.get(`/readiness-actions/${r.action_key}/history`);
                                        setHistoryKey(r.action_key);
                                        setHistoryEntries(res.data?.history || []);
                                      } catch { toast.error("No history yet"); }
                                    }}
                                    className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-sky-700"
                                    data-testid={`action-history-${i}`}
                                  >View</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Evidence Readiness</div>
                    <ul className="space-y-1" data-testid="readiness-evidence">
                      {(readiness.evidence_readiness || []).map((e) => (
                        <li key={e.area} className="text-[12px] flex items-center justify-between gap-2 py-1 border-b border-slate-100 last:border-0" data-testid={`readiness-evidence-${e.module}`}>
                          <Link to={`/${e.module}`} className="text-slate-800 hover:text-sky-700 hover:underline">{e.area}</Link>
                          <span className={`text-[11px] font-medium flex items-center gap-1 ${e.ok ? "text-emerald-700" : "text-amber-700"}`}>
                            {e.ok ? "✓" : "⚠"} {e.note}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Management summary */}
                {readiness.summary_text && (
                  <div className="px-5 py-4 border-t border-slate-100" data-testid="readiness-summary">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Management Summary</div>
                    <p className="text-[13px] text-slate-700 leading-relaxed">{readiness.summary_text}</p>
                  </div>
                )}
                {readinessHistory.length >= 2 && (
                  <div className="mt-5 pt-4 border-t border-slate-100" data-testid="readiness-history">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Readiness Trend · last {readinessHistory.length} weeks</div>
                      <div className="flex items-center gap-1 text-[10px]">
                        <button onClick={() => setHistoryMode("overall")} data-testid="trend-mode-overall" className={`px-2 py-0.5 border ${historyMode === "overall" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>Overall</button>
                        <button onClick={() => setHistoryMode("kloe")} data-testid="trend-mode-kloe" className={`px-2 py-0.5 border ${historyMode === "kloe" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>By KLOE</button>
                      </div>
                    </div>
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={historyMode === "kloe" ? kloeHistory : readinessHistory} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 2" vertical={false} />
                          <XAxis dataKey="week" tick={{ fontSize: 9, fill: "#64748B" }} interval="preserveStartEnd" />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "#64748B" }} />
                          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
                          {historyMode === "overall" ? (
                            <Line type="monotone" dataKey="score" stroke="#0369A1" strokeWidth={2} dot={{ r: 3 }} />
                          ) : (
                            <>
                              <Line type="monotone" dataKey="Safe" stroke="#DC2626" strokeWidth={1.5} dot={false} />
                              <Line type="monotone" dataKey="Effective" stroke="#0369A1" strokeWidth={1.5} dot={false} />
                              <Line type="monotone" dataKey="Caring" stroke="#059669" strokeWidth={1.5} dot={false} />
                              <Line type="monotone" dataKey="Responsive" stroke="#D97706" strokeWidth={1.5} dot={false} />
                              <Line type="monotone" dataKey="Well-led" stroke="#7C3AED" strokeWidth={1.5} dot={false} />
                            </>
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    {historyMode === "kloe" && (
                      <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-3">
                        <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-red-600" /> Safe</span>
                        <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-sky-700" /> Effective</span>
                        <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-emerald-600" /> Caring</span>
                        <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-amber-600" /> Responsive</span>
                        <span><span className="inline-block w-2 h-2 mr-1 align-middle bg-violet-600" /> Well-led</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Management Review sign-off */}
                <div className="px-5 py-4 border-t border-slate-100" data-testid="readiness-mgmt-review">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Management Review</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12px]">
                    <div>
                      <div className="text-slate-500 mb-0.5">Report reviewed by</div>
                      <div className="border-b border-slate-300 h-6" />
                    </div>
                    <div>
                      <div className="text-slate-500 mb-0.5">Role</div>
                      <div className="border-b border-slate-300 h-6" />
                    </div>
                    <div>
                      <div className="text-slate-500 mb-0.5">Review date</div>
                      <div className="border-b border-slate-300 h-6" />
                    </div>
                    <div>
                      <div className="text-slate-500 mb-0.5">Next review</div>
                      <div className="border-b border-slate-300 h-6" />
                    </div>
                  </div>
                </div>

                {/* Footer disclaimer */}
                <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 leading-relaxed rounded-b-lg" data-testid="readiness-footer-disclaimer">
                  <b>AYOBELL Care — CQC Inspection Readiness.</b> This report is generated from information recorded within AYOBELL Care and is intended to support internal governance, quality assurance and inspection preparation. The AYOBELL CQC Readiness Score is an internal assessment indicator and is <b>not an official CQC rating, inspection result or guarantee of regulatory compliance</b>. Providers remain responsible for ensuring compliance with applicable legislation, regulations and current CQC requirements.
                </div>
              </section>
              </>
            ); })()}

            {/* Action history drawer */}
            {historyKey && (
              <div
                className="fixed inset-0 z-50 bg-slate-900/40 flex items-end sm:items-center justify-center p-4"
                onClick={() => setHistoryKey(null)}
                data-testid="action-history-drawer"
              >
                <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Action History Log</div>
                    <button onClick={() => setHistoryKey(null)} className="text-slate-400 hover:text-slate-700 text-lg" data-testid="close-history">×</button>
                  </div>
                  {historyEntries.length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center">No history recorded yet.</div>
                  ) : (
                    <ol className="space-y-2" data-testid="history-entries">
                      {historyEntries.map((h, i) => (
                        <li key={i} className="border border-slate-200 rounded-md p-2.5" data-testid={`history-entry-${i}`}>
                          <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
                            <span className="font-semibold text-slate-800">{h.by_name || h.by_email || "System"}</span>
                            <span className="tabular-nums">{new Date(h.at).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                          <ul className="mt-1 space-y-0.5 text-[12px]">
                            {(h.changes || []).map((c, k) => (
                              <li key={k}>
                                <span className="text-slate-500">{c.field}: </span>
                                <span className="text-slate-500 line-through">{String(c.from ?? "—")}</span>
                                {" → "}
                                <span className="text-slate-900 font-semibold">{String(c.to ?? "—")}</span>
                              </li>
                            ))}
                          </ul>
                          {h.note && <div className="text-[11px] text-slate-500 mt-1 italic">&ldquo;{h.note}&rdquo;</div>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>
            )}

            {(missedDoses.length > 0 || nextDoses.length > 0 || todaySummary) && (
              <MedicationSafetyAlert missedDoses={missedDoses} nextDoses={nextDoses} todaySummary={todaySummary} />
            )}

            {false && missedDoses && missedDoses.length > 0 && (
              <section />
            )}

            {prnTrend && (
              <section className="bg-white border border-slate-200 p-4" data-testid="prn-trend-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <PillBottle className="w-4 h-4 text-teal-700" />
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">PRN Trend · this week vs last</div>
                  </div>
                  <Link to="/mar-chart" className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-900" data-testid="prn-trend-link">Open MAR →</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="border border-slate-200 p-3" data-testid="prn-trend-this">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">This week</div>
                    <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums" data-testid="prn-trend-this-count">{prnTrend.this_week.count}</div>
                    <div className="text-[11px] text-slate-500">since {prnTrend.this_week.start}</div>
                  </div>
                  <div className="border border-slate-200 p-3" data-testid="prn-trend-last">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Last week</div>
                    <div className="text-2xl font-heading font-semibold text-slate-700 tabular-nums" data-testid="prn-trend-last-count">{prnTrend.last_week.count}</div>
                    <div className="text-[11px] text-slate-500">from {prnTrend.last_week.start}</div>
                  </div>
                  <div className={`border p-3 ${prnTrend.trend === "up" ? "border-red-300 bg-red-50" : prnTrend.trend === "down" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="prn-trend-delta">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Change</div>
                    <div className={`text-2xl font-heading font-semibold tabular-nums ${prnTrend.trend === "up" ? "text-red-800" : prnTrend.trend === "down" ? "text-emerald-800" : "text-slate-700"}`} data-testid="prn-trend-delta-val">
                      {prnTrend.delta > 0 ? "+" : ""}{prnTrend.delta}
                    </div>
                    <div className="text-[11px] text-slate-600 uppercase tracking-widest font-semibold">{prnTrend.trend === "up" ? "trending up" : prnTrend.trend === "down" ? "trending down" : "flat"}</div>
                  </div>
                </div>
                {prnTrend.this_week.top && (
                  <div className="mt-3 text-xs text-slate-700" data-testid="prn-trend-top">
                    Most-requested this week: <span className="font-semibold">{prnTrend.this_week.top}</span> · {prnTrend.this_week.top_count} dose{prnTrend.this_week.top_count !== 1 ? "s" : ""}
                  </div>
                )}
                {prnTrendHistory && prnTrendHistory.series && (
                  <div className="mt-3" data-testid="prn-sparkline">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Last {prnTrendHistory.weeks} weeks</div>
                    <svg viewBox="0 0 240 40" className="w-full h-10" preserveAspectRatio="none">
                      {(() => {
                        const series = prnTrendHistory.series;
                        const max = Math.max(prnTrendHistory.max_count, 1);
                        const step = 240 / Math.max(series.length - 1, 1);
                        const points = series.map((s, i) => {
                          const x = i * step;
                          const y = 38 - (s.count / max) * 34;
                          return `${x},${y}`;
                        }).join(" ");
                        const bars = series.map((s, i) => {
                          const x = i * step;
                          const h = (s.count / max) * 34;
                          return <rect key={i} x={x - 2} y={38 - h} width="4" height={Math.max(h, 1)} fill={i === series.length - 1 ? "#0F766E" : "#94A3B8"} opacity={i === series.length - 1 ? 1 : 0.5}>
                            <title>{s.week_start}: {s.count}</title>
                          </rect>;
                        });
                        return (
                          <>
                            <polyline points={points} fill="none" stroke="#0F766E" strokeWidth="1.5" />
                            {bars}
                          </>
                        );
                      })()}
                    </svg>
                    <div className="flex justify-between text-[9px] text-slate-500 mt-0.5 tabular-nums">
                      <span>{prnTrendHistory.series[0]?.week_start}</span>
                      <span>{prnTrendHistory.series[prnTrendHistory.series.length - 1]?.week_start}</span>
                    </div>
                  </div>
                )}
              </section>
            )}

            {upcomingAppts && upcomingAppts.length > 0 && (
              <section className="bg-white border border-slate-200 p-4" data-testid="upcoming-appointments">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-indigo-700" />
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Upcoming Appointments · next 14 days</div>
                    <span className="text-[10px] text-slate-500" data-testid="appt-count">{upcomingAppts.length} scheduled</span>
                  </div>
                </div>
                <ul className="divide-y divide-slate-100" data-testid="appt-list">
                  {upcomingAppts.slice(0, 8).map((a) => {
                    const dt = new Date(a.scheduled_at);
                    const isToday = dt.toDateString() === new Date().toDateString();
                    const tone = isToday ? "border-l-red-500 bg-red-50" : "border-l-indigo-500 bg-indigo-50/40";
                    return (
                      <li key={a.id} data-testid={`appt-${a.id}`} className={`flex flex-col sm:flex-row items-start gap-2 sm:gap-3 px-2 py-2 border-l-2 ${tone}`}>
                        <div className="text-[11px] text-slate-600 shrink-0 sm:w-24 tabular-nums flex sm:block gap-2">
                          <div className="font-semibold text-slate-800">{dt.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}</div>
                          <div>{dt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}{a.duration_min ? ` · ${a.duration_min} min` : ""}</div>
                        </div>
                        <div className="flex-1 min-w-0 w-full">
                          <div className="text-sm font-medium text-slate-900 break-words">
                            <Link to={`/clients/${a.client_id}/appointments`} className="hover:text-indigo-700 hover:underline">{a.title}</Link>
                            <span className="text-[10px] uppercase tracking-widest text-indigo-700 border border-indigo-300 bg-white px-1.5 py-0.5 ml-2 inline-block mt-1">{a.appointment_type}</span>
                          </div>
                          <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap gap-x-3">
                            <span className="font-medium">{a.client_name}</span>
                            {a.location && <span>· {a.location}</span>}
                            {a.clinician && <span>· {a.clinician}</span>}
                            {a.escort_staff && <span>· escort: {a.escort_staff}</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {upcomingAppts.length > 8 && <li className="text-xs text-slate-500 px-2 py-2">+ {upcomingAppts.length - 8} more…</li>}
                </ul>
              </section>
            )}

            {cdDiscrepancies && cdDiscrepancies.length > 0 && (
              <section data-testid="cd-discrepancy-alert" className="bg-red-50 border border-red-300 border-l-4 border-l-red-600 p-4">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-900">
                      Controlled drug stock discrepancy — <span data-testid="cd-discrepancy-count">{cdDiscrepancies.length}</span> unresolved
                    </div>
                    <ul className="mt-2 space-y-1" data-testid="cd-discrepancy-list">
                      {cdDiscrepancies.slice(0, 3).map((e) => (
                        <li key={e.id} className="text-xs text-red-900" data-testid={`cd-discrepancy-${e.id}`}>
                          <span className="font-medium">{e.medication_name}</span> · {e.action === "received" ? `received ${e.quantity} vs expected ${e.expected_quantity}` : `counted ${e.quantity} vs expected balance ${e.expected_balance ?? "?"}`} · variance <span className="font-semibold">{e.discrepancy_delta > 0 ? `+${e.discrepancy_delta}` : e.discrepancy_delta}</span> · {new Date(e.created_at).toLocaleString()}
                        </li>
                      ))}
                      {cdDiscrepancies.length > 3 && (
                        <li className="text-xs text-red-800">+ {cdDiscrepancies.length - 3} more…</li>
                      )}
                    </ul>
                    <Link to="/cd-ledger" className="inline-block mt-2 text-xs font-medium text-red-900 hover:underline" data-testid="cd-discrepancy-link">
                      Investigate in CD ledger →
                    </Link>
                  </div>
                </div>
              </section>
            )}

            {showExpiryBanner && (
              <div
                data-testid="expiry-banner"
                className={`flex items-start gap-3 border-l-4 p-4 ${
                  stats.training.expired > 0
                    ? "border-red-500 bg-red-50"
                    : "border-amber-500 bg-amber-50"
                }`}
              >
                <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${stats.training.expired > 0 ? "text-red-600" : "text-amber-600"}`} />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-slate-900">
                    Training compliance action required
                  </div>
                  <div className="text-slate-700 mt-0.5">
                    <span className="tabular-nums font-semibold text-red-700">{stats.training.expired}</span> expired ·{" "}
                    <span className="tabular-nums font-semibold text-amber-700">{stats.training.expiring_soon}</span> expiring within 30 days.{" "}
                    <Link to="/training" className="text-sky-700 hover:underline font-medium" data-testid="expiry-banner-link">Review training records →</Link>
                  </div>
                </div>
              </div>
            )}

            <section data-testid="kpi-safety">
              <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-3 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" /> Safety & Clinical Risk · highest CQC priority
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <KpiCard
                  testid="kpi-incidents" to="/incidents" priority="P0"
                  icon={AlertOctagon} label="Open Incidents"
                  value={stats.incidents.open}
                  sub={`${stats.incidents.safeguarding_open} safeguarding open`}
                  tone={stats.incidents.safeguarding_open > 0 ? "danger" : stats.incidents.open > 0 ? "warn" : "ok"}
                />
                <KpiCard
                  testid="kpi-medication" to="/medication-errors" priority="P0"
                  icon={PillBottle} label="Medication Errors 30d"
                  value={stats.medication_errors.last_30_days}
                  sub={`${stats.medication_errors.total} total logged`}
                  tone={stats.medication_errors.last_30_days > 0 ? "danger" : "ok"}
                />
                <KpiCard
                  testid="kpi-training" to="/training" priority="P0"
                  icon={GraduationCap} label="Training Compliance"
                  value={`${stats.training.compliance_pct}%`}
                  sub={`${stats.training.expired} expired · ${stats.training.expiring_soon} expiring 30d`}
                  tone={stats.training.expired > 0 ? "danger" : stats.training.expiring_soon > 0 ? "warn" : "ok"}
                />
                <KpiCard
                  testid="kpi-maintenance" to="/maintenance" priority="P1"
                  icon={Wrench} label="Maintenance Open"
                  value={stats.maintenance.open}
                  sub={`${stats.maintenance.overdue} overdue`}
                  tone={stats.maintenance.overdue > 0 ? "danger" : stats.maintenance.open > 0 ? "warn" : "ok"}
                />
                <KpiCard
                  testid="kpi-audits" to="/audits" priority="P1"
                  icon={ShieldCheck} label="Open Audits"
                  value={stats.audits.open} sub={`${stats.audits.total} total`}
                  tone={stats.audits.open > 5 ? "warn" : "ok"}
                />
              </div>
            </section>

            <section data-testid="kpi-governance">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-3 flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> Governance & People
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <KpiCard
                  testid="kpi-supervisions" to="/supervisions" priority="P1"
                  icon={ClipboardCheck} label="Supervisions"
                  value={stats.supervisions.total}
                  sub={`${stats.supervisions.overdue} overdue`}
                  tone={stats.supervisions.overdue > 0 ? "warn" : "ok"}
                />
                <KpiCard
                  testid="kpi-complaints" to="/complaints" priority="P1"
                  icon={MessageSquareWarning} label="Complaints Open"
                  value={stats.complaints.open}
                  sub={`${stats.complaints.compliments_last_30_days} compliments 30d`}
                  tone={stats.complaints.open > 0 ? "warn" : "ok"}
                />
                <KpiCard
                  testid="kpi-outcomes" to="/outcomes" priority="P2"
                  icon={Target} label="Outcomes Achieved"
                  value={`${stats.outcomes.achieved}/${stats.outcomes.total || 0}`}
                  sub="Service user goals" tone="ok"
                />
                <KpiCard
                  testid="kpi-clients" to="/clients"
                  icon={HeartHandshake} label="Service Users"
                  value={stats.clients.total} sub="On the books" tone="info"
                />
                <KpiCard
                  testid="kpi-staff" to="/staff"
                  icon={Users} label="Staff"
                  value={stats.staff.total} sub="Active employees" tone="info"
                />
              </div>
            </section>

            {trends && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Trends</div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <ChartCard testid="chart-incidents" title="Incidents · Last 30 days" subtitle="Daily count">
                    <LineChart data={trends.incidents_30d} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 2" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: "#64748B" }}
                        tickFormatter={(v) => v.slice(5)}
                        interval={4}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
                      <Line type="monotone" dataKey="count" stroke="#DC2626" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ChartCard>

                  <ChartCard testid="chart-medication" title="Medication errors · Last 6 months" subtitle="Monthly count">
                    <BarChart data={trends.medication_6m} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 2" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
                      <Bar dataKey="count" fill="#D97706" radius={0} />
                    </BarChart>
                  </ChartCard>

                  <ChartCard testid="chart-expiries" title="Training expiries · Next 6 months" subtitle="Certificates due to renew">
                    <BarChart data={trends.training_expiries_6m} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 2" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #E2E8F0" }} />
                      <Bar dataKey="count" fill="#0369A1" radius={0} />
                    </BarChart>
                  </ChartCard>
                </div>
              </section>
            )}

            {handover && (
              <section data-testid="handover-section">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3 flex items-center gap-1.5">
                  <Radio className="w-3 h-3" /> Shift handover · Last 24 hours
                </div>
                <div className="bg-white border border-slate-200">
                  {handover.items.length === 0 ? (
                    <div className="p-6 text-sm text-slate-500 text-center" data-testid="handover-empty">Nothing to hand over in the last 24 hours.</div>
                  ) : (
                    <ul className="divide-y divide-slate-100" data-testid="handover-list">
                      {handover.items.map((it, i) => {
                        const Icon = {
                          "daily-log": NotebookPen,
                          "incident": AlertOctagon,
                          "medication-error": PillBottle,
                          "emar": Pill,
                          "visit": LogInIcon,
                        }[it.kind] || NotebookPen;
                        const tone = {
                          "incident": "text-red-600",
                          "medication-error": "text-red-600",
                          "emar": "text-emerald-700",
                          "visit": "text-sky-700",
                          "daily-log": "text-slate-600",
                        }[it.kind] || "text-slate-600";
                        return (
                          <li key={i} className="p-3 flex items-start gap-3" data-testid={`handover-item-${i}`}>
                            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} strokeWidth={1.75} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <div className="text-sm font-medium text-slate-900 truncate">{it.title}</div>
                                {it.severity && <span className="text-[10px] uppercase tracking-widest text-red-700">· {it.severity}</span>}
                              </div>
                              {it.detail && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{it.detail}</div>}
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {it.at ? new Date(it.at).toLocaleString() : ""}{it.by ? ` · ${it.by}` : ""}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>
            )}

            {/* Personal + storage row — compact cards side-by-side on desktop,
                stacked on tablet/mobile. Kept in the lower dashboard so the
                Executive AI Briefing + operational information dominate the
                top of the page. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch" data-testid="dashboard-lower-row">
              <DashboardHolidayWidget />
              {usage && (
                <section data-testid="storage-usage" className={`bg-white border p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 h-full ${usage.alert_triggered ? "border-red-400 bg-red-50" : "border-slate-200"}`}>
                  <div className={`w-10 h-10 border flex items-center justify-center shrink-0 ${usage.alert_triggered ? "border-red-300 text-red-700" : "border-slate-200 text-slate-600"}`}>
                    <HardDrive className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                      Storage used this month{usage.alert_triggered && <span className="ml-2 text-red-700">· Alert threshold reached</span>}
                    </div>
                    <div className="font-heading text-lg font-semibold tabular-nums" data-testid="storage-month-bytes">
                      {(usage.month_bytes / (1024 * 1024)).toFixed(1)} MB
                      <span className="text-xs text-slate-500 font-normal"> · {usage.month_count} file{usage.month_count === 1 ? "" : "s"}</span>
                      <span className="text-xs text-slate-500 font-normal"> / {usage.alert_threshold_mb} MB alert</span>
                    </div>
                    <div className="text-[11px] text-slate-500">All-time: {(usage.total_bytes / (1024 * 1024)).toFixed(1)} MB across {usage.total_count} file{usage.total_count === 1 ? "" : "s"}</div>
                  </div>
                  {isManager && cleanupPreview && cleanupPreview.eligible_count > 0 && (
                    <div className="flex flex-col sm:items-end gap-1 shrink-0 w-full sm:w-auto pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200" data-testid="storage-cleanup">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Purge available</div>
                      <div className="text-xs text-slate-700">
                        <span data-testid="cleanup-eligible-count" className="font-semibold">{cleanupPreview.eligible_count}</span> file{cleanupPreview.eligible_count === 1 ? "" : "s"} · {(cleanupPreview.eligible_bytes / (1024 * 1024)).toFixed(1)} MB
                      </div>
                      {user?.role === "admin" && (
                        <Button size="sm" variant="outline" onClick={runCleanup} data-testid="btn-storage-cleanup" className="h-8">
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Purge &gt; 90 days
                        </Button>
                      )}
                    </div>
                  )}
                </section>
              )}
            </div>

            {isManager && pushHistory.length > 0 && (
              <section className="bg-white border border-slate-200" data-testid="push-history-section">
                <button
                  className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-slate-50"
                  onClick={() => setPushHistoryOpen((v) => !v)}
                  data-testid="btn-push-history-toggle"
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                    <BellRing className="w-3 h-3" /> Push history · last {pushHistory.length} attempts
                  </div>
                  <span className="text-[10px] text-slate-500">{pushHistoryOpen ? "Collapse" : "Expand"}</span>
                </button>
                {pushHistoryOpen && (
                  <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto" data-testid="push-history-list">
                    {pushHistory.map((p) => {
                      const toneClass = p.status === "sent" ? "text-emerald-700 bg-emerald-50 border-emerald-300"
                        : p.status === "pruned" ? "text-slate-700 bg-slate-100 border-slate-300"
                        : "text-red-700 bg-red-50 border-red-300";
                      return (
                        <li key={p.id} className="px-4 py-2 flex items-start gap-3" data-testid={`push-history-${p.id}`}>
                          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border shrink-0 mt-0.5 ${toneClass}`} data-testid={`push-status-${p.id}`}>{p.status}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{p.title || "(no title)"}</div>
                            {p.body && <div className="text-xs text-slate-600 truncate">{p.body}</div>}
                            <div className="text-[11px] text-slate-500">
                              {p.at ? new Date(p.at).toLocaleString() : ""}
                              {p.user_email ? ` · ${p.user_email}` : (p.portal_token ? ` · family portal` : "")}
                              {p.tag ? ` · ${p.tag}` : ""}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            )}

            {stats?.emar_today && (
              <section className="bg-white border border-slate-200 p-4 flex flex-wrap items-center gap-4 sm:gap-6" data-testid="emar-today">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5 w-full sm:w-auto">
                  <PillBottle className="w-3 h-3" /> Medications today
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-2xl font-semibold text-emerald-700 tabular-nums" data-testid="emar-today-administered">{stats.emar_today.administered}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">administered</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-2xl font-semibold text-amber-700 tabular-nums" data-testid="emar-today-refused">{stats.emar_today.refused}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">refused</span>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-2xl font-semibold text-slate-500 tabular-nums" data-testid="emar-today-withheld">{stats.emar_today.withheld}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">withheld</span>
                </div>
                <Link to="/mar-chart" className="sm:ml-auto text-xs text-sky-700 hover:underline min-h-[44px] inline-flex items-center" data-testid="link-mar-chart">Open MAR chart →</Link>
              </section>
            )}

            <section className="mt-4 lg:mt-8 bg-white border border-slate-200 p-4 lg:p-6">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Getting to Outstanding</div>
              <p className="text-sm text-slate-700 max-w-3xl leading-relaxed">
                Ayobell Care consolidates the eight systems CQC inspectors expect to see when a service is rated Outstanding. Log
                findings, incidents, or complaints in the relevant module — then click <span className="font-medium">Generate action plan</span> to
                get a SMART, KLOE-aware plan drafted by Claude Sonnet 4.5. Attach evidence (certificates, incident photos, audit
                documents) directly to any record via the detail pane.
              </p>
            </section>
          </>
        )}
      </div>

      {/* Readiness sign-off modal — mounted at page root so it overlays everything */}
      <ReadinessSignoffDialog
        open={readinessSignoffOpen}
        onOpenChange={setReadinessSignoffOpen}
        readiness={readiness}
        user={user}
        onSigned={(rec) => {
          setLatestSignoff({
            id: rec.id, signed_at: rec.signed_at, signed_by_name: rec.signed_by_name,
            signed_by_role: rec.signed_by_role, score: rec.score, band: rec.band,
            band_label: rec.band_label, priority_actions_count: rec.priority_actions_count,
            areas_performing_strongly: rec.areas_performing_strongly,
            areas_requiring_attention: rec.areas_requiring_attention,
            site_id: rec.site_id, comment: rec.comment,
            assessment_generated_at: rec.assessment_generated_at,
          });
          api.get("/dashboard/readiness/signoffs").then((r) => setSignoffHistory(r.data || []));
        }}
      />
    </div>
  );
}
